/**
 * Renders Google's own sign-in button and hands the resulting ID token to the
 * app's Google login action.
 *
 * The button must be rendered by Google Identity Services itself — GIS only
 * issues a credential from a control it owns — so this is a container that GIS
 * fills, not a styled button of ours.
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { googleSignIn } from '../../store/actions/authActions';

const GoogleSignInButton = ({ organization, country, acceptTerms, onError, googleSignInAction }) => {
  const history = useHistory();
  const containerRef = useRef(null);
  const [unavailable, setUnavailable] = useState('');
  // Read through a ref so re-rendering the button is not tied to field edits.
  const profileRef = useRef({ organization, country, acceptTerms });
  profileRef.current = { organization, country, acceptTerms };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { renderGoogleButton } = await import('../../services/googleAuth');
        if (cancelled || !containerRef.current) return;

        await renderGoogleButton(containerRef.current, {
          width: containerRef.current.offsetWidth || undefined,
          onToken: async (idToken) => {
            try {
              const data = await googleSignInAction({
                idToken,
                organization: profileRef.current.organization,
                country: profileRef.current.country,
                acceptTerms: profileRef.current.acceptTerms,
              });
              history.push(data.redirectTo || '/subscription');
            } catch (err) {
              if (onError) onError(err.message || 'Google sign-in failed.', err);
            }
          },
          onError: (err) => {
            if (onError) onError(err.message, err);
          },
        });
      } catch (err) {
        if (!cancelled) setUnavailable(err.message || 'Google sign-in is unavailable.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [googleSignInAction, history, onError]);

  return (
    <div className="xk-gate__google-wrap">
      <div ref={containerRef} className="xk-gate__google-slot" />
      {unavailable && <div className="xk-gate__error">{unavailable}</div>}
    </div>
  );
};

GoogleSignInButton.propTypes = {
  organization: PropTypes.string,
  country: PropTypes.string,
  acceptTerms: PropTypes.bool,
  onError: PropTypes.func,
  googleSignInAction: PropTypes.func.isRequired,
};

export default connect(null, { googleSignInAction: googleSignIn })(GoogleSignInButton);
